import store from '../store'
import {setPermissions} from '../utils/permissions'
import { getUserPortalDetials, getLoginDetails } from '../app/auth/services'
import { recipientAdUrl, adUrl } from '../config'
import { POST_LOGIN_REDIRECT_KEY, safePostLoginRedirect } from '../utils/lmsRedirect'


/**
* Preserve an approver/creator "Review" deep-link that we are about to bounce to the
* dashboard because permissions have not loaded yet — the deep-link arrived right after
* login, before the dashboard/sidebar fetched /setting/user_modules. The dashboard
* forwards it once permissions exist (app/dashboard/main.vue -> forwardPendingDeeplink()).
* Guarded by !permissions so it only fires transiently post-login, never for a genuine
* no-access user (who already has permissions loaded). Called from EVERY protected
* permission-fail branch below — the generic gate AND the announcement/policy special
* cases (the announcement/policy preview aliases go through their own branch, so they
* need this too, otherwise those deep-links are silently dropped on the bounce).
*/
function preservePendingDeeplink(to) {
    try {
        var target = (to && (to.fullPath || to.path)) || '';
        if (safePostLoginRedirect(target) && !localStorage.getItem('permissions')) {
            sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, target);
        }
    } catch (e) { /* noop */ }
}


/**
* Extract subdomain from current hostname
*/
function getSubdomain() {
    var regexParse = new RegExp("([a-z\\-0-9]{2,63})\\.([a-z\\.]{2,5})$");
    var urlParts = regexParse.exec(window.location.hostname);
    if (!urlParts) {
        return "";
    }
    var domain = urlParts[1];
    var type = urlParts[2];
    return window.location.hostname
        .replace(domain + "." + type, "")
        .slice(0);
}

/**
* Check if AD auto-redirect is enabled for userportal login.
* If enabled, redirect to AD auth URL before the component mounts.
*/
function checkAdAutoRedirect(to, next) {
    var query = to.query || {};
    // Skip redirect if user is coming back with error or after logout
    if (query.errorCode || query.logout === 'true' || query.logout === true) {
        next();
        return;
    }

    var subdomain = getSubdomain();
    getUserPortalDetials({ subdomain: subdomain })
        .then(function(response) {
            // Cache response so userportal.vue doesn't call API again
            window.__userPortalDetailsCache = response;
            if (response.data && response.data.value && response.data.value.enable_ad_auth_redirect == true) {
                // Redirect to AD auth URL — component will never mount
                window.location.href = recipientAdUrl + "/" + btoa(subdomain);
                // Do not call next() — navigation is cancelled, full page redirect happens
            } else {
                next();
            }
        })
        .catch(function() {
            // On API error, let the login page load normally
            next();
        });
}

/**
* Check if AD auto-redirect is enabled for admin login.
* If enabled, redirect to AD auth URL before the component mounts.
*/
function checkAdminAdAutoRedirect(to, next) {
    var query = to.query || {};
    // Skip redirect if user is coming back with error or after logout
    if (query.errorCode || query.logout === 'true' || query.logout === true) {
        next();
        return;
    }

    var subdomain = getSubdomain();
    getLoginDetails({ subdomain: subdomain })
        .then(function(response) {
            // Cache response so login.vue doesn't call API again
            window.__adminLoginDetailsCache = response;
            if (response.data && response.data.value && response.data.value.enable_ad_auth_redirect == true) {
                // Redirect to AD auth URL — component will never mount
                window.location.href = adUrl + "/" + btoa(subdomain);
            } else {
                next();
            }
        })
        .catch(function() {
            // On API error, let the login page load normally
            next();
        });
}

const beforeEach = (to, from, next) => {
    let permissions= setPermissions()

    const auth = to.meta.requiresAuth == undefined;
    window.scrollTo(0, 0)
    /**
    * Clears all global feedback message
    * that might be visible
    */
    store.dispatch('resetMessages')

    /**
    * If route doesn't require authentication be normally accessed.
    */
    if (auth !== true) {
        // If navigating to userportal login, check AD auto-redirect first
        if (to.name === 'auth.userportalLogin') {
            checkAdAutoRedirect(to, next);
            return;
        }

        // If navigating to admin login, check AD auto-redirect first
        if (to.name === 'auth.login') {
            checkAdminAdAutoRedirect(to, next);
            return;
        }

        const userLogin=JSON.parse(localStorage.getItem('user'))
        const notAuthorizedRoutes=['/login','/register','/verification','/reset','/saml']
        if(userLogin !==null || userLogin )
        {
            if(notAuthorizedRoutes.includes(to.path)){
            next({ name: 'dashboard.index' })
            }
            else{
                next()
                }
        }
        else{
            next()
            }
            return
       // return to prevent the code from continuing in its flow
        // With this flow `else` or `else if` is not necessary
    }

    /**
    * Otherwise  if authentication is required login.
    */  
    store.dispatch('checkUserToken')
    .then(() => {
        // There is a token and it is valid
        // Allow Announcement/Policy paths based on their own permissions to avoid alias/name conflicts
        try {
            const userPerms = store.state.permissions && store.state.permissions();
            const path = to.path || '';

            if (path.indexOf('/announcements') === 0) {
                if (userPerms && (userPerms.announcement_create || userPerms.announcement_view)) {
                    next();
                } else {
                    preservePendingDeeplink(to);
                    next({ name: 'dashboard.index' });
                }
                return;
            }

            if (path.indexOf('/policy') === 0) {
                if (userPerms && (userPerms.policy_create || userPerms.policy_view)) {
                    next();
                } else {
                    preservePendingDeeplink(to);
                    next({ name: 'dashboard.index' });
                }
                return;
            }
        } catch (e) {
            // fall through
        }
        if(permissions[to.path]){
            next()
        }else{
            try{
                if(permissions[to.name]){
                    next()
                }else{
                    preservePendingDeeplink(to);
                    next({ name: 'dashboard.index' })
                }
            }catch(e){
                // console.log(e, 'errerererererer')
            }
        }
         
         // can access the route
    })
    .catch(() => {
        // No token, or it is invalid.
        // Approver "Review & Approve" deep-links (campaign / announcement / policy
        // preview) are admin-panel features. Remember the intended path and send the
        // approver to the ADMIN login so they are returned to the preview after login
        // (mirrors the LMS approval deep-link flow). The stash is sessionStorage, so it
        // survives the localStorage.clear() below AND any external SSO/2FA round-trip.
        try {
            var intended = to.fullPath || to.path || '';
            if (safePostLoginRedirect(intended)) {
                sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, intended);
                localStorage.clear();
                next({ name: 'auth.login' }); // approver features live in the admin panel
                return;
            }
        } catch (e) { /* noop — fall through to default login */ }
        // next({ name: 'auth.login' }) // redirect to login
        next({ name: 'auth.userportalLogin' }) //redirect to userportal login
        localStorage.clear();
    })
}

export default beforeEach



// WEBPACK FOOTER //
// ./src/router/beforeEach.js